import React, { useState, useMemo, useCallback } from 'react';
import { Typography, Button, Box, useTheme, useMediaQuery, Fade, Grow } from '@mui/material';
import { Add } from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import { UserCard, UsersTable, UserStats } from '../components/users';
import SearchBar from '../components/common/SearchBar';
import UserFormModal from '../components/forms/UserFormModal';
import { useUsers, useSearchAndFilter } from '../hooks';
import usePageOperations from '../hooks/usePageOperations';
import { filterUsers } from '../utils/userUtils';
import { THEME_COLORS, BREAKPOINTS } from '../constants';

const UsersPageComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));

  const usersHook = useUsers();
  const { users, stats, loading, error, pagination, goToPage, changePageSize } = usersHook;

  const {
    openCreateModal,
    openEditModal,
    openDeleteModal,
    FormModal,
    DeleteModal,
    OperationModal,
  } = usePageOperations({
    entityName: 'Usuario',
    entityNamePlural: 'Usuarios',
    crudFunctions: {
      create: usersHook.createUser,
      update: usersHook.updateUser,
      delete: usersHook.deleteUser,
    },
    FormModalComponent: UserFormModal,
    entityIdKey: 'user_id',
  });

  const {
    searchInput,
    setSearchInput,
    searchTerm,
    handleSearch,
    handleClearSearch,
    handleKeyPress,
  } = useSearchAndFilter();

  const [expandedUserId, setExpandedUserId] = useState(null);

  const filteredUsers = useMemo(() => filterUsers(users, searchTerm), [users, searchTerm]);

  const handleExpand = useCallback((userId) => {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  }, []);

  if (error) {
    return (
      <AppLayout currentPage="users">
        <Box sx={{ paddingY: 4 }}>
          <Typography variant="h6" color="error" align="center">
            Error al cargar usuarios: {error}
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="users">
      <Fade in timeout={500}>
        <Box>
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h4" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Gestión de Usuarios
            </Typography>
            <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}>
              Administra los roles, permisos y accesos de los usuarios del sistema.
            </Typography>
          </Box>

          <Grow in timeout={700}>
            <Box>
              <UserStats stats={stats} />
            </Box>
          </Grow>

          <Grow in timeout={800}>
            <Box sx={{ my: 4 }}>
              <SearchBar
                searchInput={searchInput}
                searchTerm={searchTerm}
                placeholder="Buscar usuarios por nombre, correo o celular..."
                onSearchInputChange={setSearchInput}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                onKeyPress={handleKeyPress}
              />
            </Box>
          </Grow>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateModal}
              sx={{
                backgroundColor: THEME_COLORS.TEXT_PRIMARY, color: THEME_COLORS.BACKGROUND_PAPER, borderRadius: '12px',
                paddingX: 3, paddingY: 1.5, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#1a1a1a' }
              }}
            >
              Nuevo Usuario
            </Button>
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {filteredUsers.map(u => (
                <UserCard
                  key={u.user_id}
                  user={u}
                  onEdit={() => openEditModal(u)}
                  onDelete={() => openDeleteModal(u)}
                  isExpanded={expandedUserId === u.user_id}
                  onExpand={handleExpand}
                  showActions={true}
                />
              ))}
            </Box>
          ) : (
            <UsersTable
              users={filteredUsers}
              expandedUserId={expandedUserId}
              onExpand={handleExpand}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              showActions={true}
              // Props de paginación
              pagination={pagination}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
            />
          )}
        </Box>
      </Fade>
      
      <FormModal />
      <DeleteModal />
      <OperationModal />
    </AppLayout>
  );
};

const UsersPage = React.memo(UsersPageComponent);
UsersPage.displayName = 'UsersPage';

export default UsersPage; 